import { useEffect, useRef, useState } from 'react';
import { Check, AlertCircle, Loader2 } from 'lucide-react';
import { avaliarNcm, digitosNcm, motivoNcmInvalido } from '@fiodecobre/shared';
import { api } from '../lib/api';

interface NcmItem {
  codigo: string;
  descricao?: string;
  descricao_completa?: string;
}

interface Props {
  value: string;
  onChange: (codigo: string) => void;
  placeholder?: string;
}

// Texto que o operador esta digitando como CODIGO (digitos e a pontuacao que ele usa:
// `8536.50.90`). String vazia casa de proposito — apagar o campo e intencao legitima.
// Qualquer letra derruba: ali ele esta buscando por DESCRICAO, e entregar os digitos
// disso apagaria o NCM gravado (`digitosNcm('interruptor')` e vazio).
const PARECE_CODIGO = /^[\d.\-\s/]*$/;

export function AutocompleteNCM({ value, onChange, placeholder = 'Codigo ou descricao' }: Props) {
  const [busca, setBusca] = useState(value || '');
  const [aberto, setAberto] = useState(false);
  const [resultados, setResultados] = useState<NcmItem[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [existeNaTabela, setExisteNaTabela] = useState<boolean | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastValidatedRef = useRef('');
  // Ultimo valor que ESTE componente entregou ao pai. Sem isso o efeito de sincronia
  // abaixo devolveria ao campo o texto ja normalizado e comeria a pontuacao enquanto
  // o operador ainda digita (`8536.5` viraria `85365` embaixo do cursor).
  const emitidoRef = useRef<string | null>(null);

  function emitir(codigo: string) {
    if (codigo === String(value ?? '')) return;
    emitidoRef.current = codigo;
    onChange(codigo);
  }

  // Sincroniza o campo quando o valor muda POR FORA (carregou outro produto, o form
  // resetou). Mudanca que saiu daqui e ignorada — quem manda no texto e o operador.
  useEffect(() => {
    if (value === emitidoRef.current) return;
    setBusca(value || '');
  }, [value]);

  // Confere na tabela oficial de NCM. So vale a pena perguntar quando o tamanho ja
  // passa no schema — antes disso o veredito e local e nao precisa de rede.
  useEffect(() => {
    const cod = digitosNcm(value);
    if (avaliarNcm(value) !== 'valido') { setExisteNaTabela(null); return; }
    if (lastValidatedRef.current === cod) return;
    lastValidatedRef.current = cod;
    api.get(`/api/ncm-oficial/${cod}`).then(() => setExisteNaTabela(true)).catch(() => setExisteNaTabela(false));
  }, [value]);

  useEffect(() => {
    function fora(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);

  function buscarRemoto(q: string) {
    if (!q || q.trim().length < 2) { setResultados([]); return; }
    setCarregando(true);
    api.get<NcmItem[]>('/api/ncm-oficial', { query: { q, limit: 20 } })
      .then((r) => setResultados(r || []))
      .catch(() => setResultados([]))
      .finally(() => setCarregando(false));
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    setBusca(v);
    setAberto(true);
    setHighlight(0);
    // ⚠️ Entrega o valor A CADA TECLA, nao so no blur/selecao.
    //
    // Ate 02/09/2026 o commit era so em `selecionar()` e `onBlurInput()`. O efeito era
    // assimetrico e mordeu de verdade: DIGITAR nao sujava o formulario (entao o Salvar
    // ficava apagado e a tela parecia travada), mas LIMPAR o campo sujava (o blur
    // entregava vazio) — ou seja, o botao acendia pra apagar e ficava apagado pra
    // corrigir. Foi assim que o NCM do produto 965 foi a branco em producao.
    if (PARECE_CODIGO.test(v)) emitir(digitosNcm(v));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buscarRemoto(v), 300);
  }

  function selecionar(item: NcmItem) {
    emitir(item.codigo);
    setBusca(item.codigo);
    setAberto(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!aberto && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      setAberto(true);
      if (resultados.length === 0) buscarRemoto(busca);
      return;
    }
    if (!aberto) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((i) => Math.min(i + 1, resultados.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (resultados[highlight]) selecionar(resultados[highlight]);
    } else if (e.key === 'Escape') {
      setAberto(false);
    }
  }

  useEffect(() => {
    if (!aberto || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el) (el as HTMLElement).scrollIntoView({ block: 'nearest' });
  }, [highlight, aberto]);

  // Rede pro caminho que o `onInputChange` nao cobre: colar uma descricao e sair, ou
  // texto misto. Digitar codigo ja entregou tecla a tecla.
  function onBlurInput(e: React.FocusEvent<HTMLInputElement>) {
    if (ref.current?.contains(e.relatedTarget as Node)) return;
    setTimeout(() => {
      setAberto(false);
      if (PARECE_CODIGO.test(busca)) emitir(digitosNcm(busca));
    }, 150);
  }

  const estado = avaliarNcm(value);
  const invalido = estado === 'invalido' || existeNaTabela === false;
  const okMesmo = estado === 'valido' && existeNaTabela === true;

  const motivo = estado === 'invalido'
    ? motivoNcmInvalido(value)
    : existeNaTabela === false
      ? `NCM ${digitosNcm(value)} nao consta na tabela oficial.`
      : null;

  const corBorda = okMesmo ? 'border-green-400' : invalido ? 'border-orange-400' : 'border-gray-300';

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <input
          type="text"
          value={busca}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          onFocus={() => { setAberto(true); if (resultados.length === 0 && busca) buscarRemoto(busca); }}
          onBlur={onBlurInput}
          placeholder={placeholder}
          aria-invalid={invalido}
          className={`w-full px-3 py-1.5 pr-7 border ${corBorda} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          {carregando ? <Loader2 size={14} className="animate-spin text-gray-400" /> :
           okMesmo ? <Check size={14} className="text-green-600" aria-label="NCM valido" /> :
           invalido ? <AlertCircle size={14} className="text-orange-500" aria-label="NCM invalido" /> : null}
        </div>
      </div>
      {/* ⚠️ O motivo vai ESCRITO, nao so na cor da borda e no icone: verde e laranja sao
          o par que o Ruan nao separa, e icone mudo nao diz o que corrigir. */}
      {motivo && (
        <div className="mt-1 text-[11px] font-semibold text-orange-600 leading-tight">{motivo}</div>
      )}
      {aberto && (
        <div ref={listRef} className="absolute z-50 left-0 right-0 top-full mt-0.5 bg-white border border-gray-300 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {carregando && resultados.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-500 flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Buscando...
            </div>
          )}
          {!carregando && resultados.length === 0 && busca.trim().length >= 2 && (
            <div className="px-3 py-2 text-xs text-gray-400">Nenhum NCM encontrado</div>
          )}
          {!carregando && resultados.length === 0 && busca.trim().length < 2 && (
            <div className="px-3 py-2 text-xs text-gray-400">Digite ao menos 2 caracteres</div>
          )}
          {resultados.map((item, idx) => {
            const isHl = idx === highlight;
            return (
              <button
                key={item.codigo}
                type="button"
                data-idx={idx}
                onMouseEnter={() => setHighlight(idx)}
                onClick={() => selecionar(item)}
                className={`w-full text-left px-3 py-2 text-xs border-b border-gray-100 last:border-b-0 ${
                  isHl ? 'bg-blue-100' : 'hover:bg-blue-50'
                }`}
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-mono font-bold text-blue-700 flex-shrink-0">{item.codigo}</span>
                  <span className="text-gray-700 line-clamp-2">{item.descricao_completa || item.descricao}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
